import { ButtonProps } from '@everlywell/ui-kit';
import { VideoComponentProps } from 'components/VideoComponent';
import { Link } from 'gatsby';
import { isFullUrl } from 'utils/helpers';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import { FlagsType, ImageType } from 'utils/types';
import { Subsections } from 'utils/types/contentfulTypes';

import { BulletPointWithIconProps } from './components/BulletPointWithIcon';

type ParsedMedia = {
  desktopImage: ImageType | null;
  mobileImage: ImageType | null;
};

type ParsedVideo = {
  mobileVideo: VideoComponentProps | null;
  desktopVideo: VideoComponentProps | null;
};

export const getMobileAndDesktopFirstImage = (
  images:
    | Queries.Maybe<readonly Queries.Maybe<Queries.ContentfulMedia001>[]>
    | Queries.pdpSectionFragment['images'],
): ParsedMedia => {
  let desktopImage: ImageType | null = null;
  let mobileImage: ImageType | null = null;

  if (images && images.length > 0) {
    if (images[0]?.desktopImage?.gatsbyImageData) {
      desktopImage = {
        gatsbyImageData: images[0]?.desktopImage?.gatsbyImageData,
        description: images[0]?.desktopImageAltText || '',
      };
    }

    if (images[0]?.mobileImage?.gatsbyImageData) {
      mobileImage = {
        gatsbyImageData: images[0]?.mobileImage?.gatsbyImageData,
        description: images[0]?.mobileImageAltText || '',
      };
    }
  }

  return { desktopImage, mobileImage };
};

export const getMobileAndDesktopAllImages = (
  images: Queries.Maybe<readonly Queries.Maybe<Queries.ContentfulMedia001>[]>,
): ParsedMedia[] =>
  images ? images?.map((image) => getMobileAndDesktopFirstImage([image])) : [];

export const getMobileAndDesktopFirstVideo = (
  media:
    | Queries.Maybe<readonly Queries.Maybe<Queries.ContentfulMedia001>[]>
    | Queries.pdpSectionFragment['images'],
): ParsedVideo => {
  let desktopVideo: VideoComponentProps | null = null;
  let mobileVideo: VideoComponentProps | null = null;

  if (media && media.length > 0) {
    if (media[0]?.desktopVideoFile?.file?.url) {
      desktopVideo = {
        url: media[0]?.desktopVideoFile?.file?.url,
        contentType:
          media[0]?.desktopVideoFile?.file?.contentType || 'video/mp4',
      };
    }

    if (media[0]?.mobileVideoFile?.file?.url) {
      mobileVideo = {
        url: media[0]?.mobileVideoFile?.file?.url,
        contentType:
          media[0]?.mobileVideoFile?.file?.contentType || 'video/mp4',
      };
    }
  }

  return { desktopVideo, mobileVideo };
};

export const findComponentByName = (
  subsections: Subsections[] | undefined,
  componentName: string,
): Subsections | undefined =>
  subsections?.find((subsection) => subsection.componentName === componentName);

export const findContentfulComponentByName = (
  componentName: string,
  subsections: Queries.ContentfulSection001[] | undefined | null,
): Queries.ContentfulSection001 | undefined | null =>
  subsections?.find(
    (subsection: any) =>
      subsection?.componentName?.toLowerCase() === componentName.toLowerCase(),
  );

export const filterSubsectionsByName = <
  T extends { componentName?: string | null },
>(
  componentName: string,
  subsections: T[] | undefined,
): T[] | undefined =>
  subsections?.filter(
    (subsection) =>
      subsection?.componentName?.toLowerCase() === componentName.toLowerCase(),
  );

export function createSharedButtonProps(url: string | undefined): ButtonProps {
  const sharedButtonProps: ButtonProps = {
    // Render a regular anchor tag if the URL is a full URL
    as: isFullUrl(url) ? 'a' : Link,
    ...(isFullUrl(url)
      ? { href: url }
      : { to: replaceRedirectionLinkRoot(url || '') }),
  };

  return sharedButtonProps;
}

export const mapBulletPointWithIcon = (
  bullet: Queries.ContentfulSection001,
): BulletPointWithIconProps => {
  const { metadata } = bullet || {};
  const { title, context } = metadata || {};

  return {
    text: title || null,
    iconName: context?.iconName || null,
    iconColor: context?.iconColor,
  };
};

export const mapBulletPointsWithIcon = (
  bulletPoints: Queries.ContentfulSection001[] | undefined,
): BulletPointWithIconProps[] =>
  bulletPoints
    ? bulletPoints?.map((bullet: Queries.ContentfulSection001) =>
        mapBulletPointWithIcon(bullet),
      )
    : [];

export const transformToLowercase = (text: string): string => {
  const words = text.split(' ');
  const transformedWords = words.map((word) => word.toLowerCase());
  return transformedWords.join('');
};

export const findContentfulFlagValue = (flagName: string, flags: FlagsType) => {
  const foundFlag = flags?.find(
    (flag) =>
      transformToLowercase(flag?.displayName ?? '') ===
      transformToLowercase(flagName),
  );

  return foundFlag?.value || false;
};
